import {useState, useEffect, useRef} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {useRouter} from 'next/router'
import Link from 'next/link'
import {decode} from 'jsonwebtoken'
import {map, always, get} from '@cullylarson/f'
import {isDev} from '@@client/lib/util'
import {sendEvent} from '@@client/lib/gtm'
import {routeToTop} from '@@client/lib/nextjs'
import {isEmptyObject} from '@@client/lib/objs'
import {useIsUser, useAccountInfo, useTokenCache} from '@@client/lib/hooks'
import {getStampS} from '@@client/lib/dates'
import {
    compareSteps,
    SHIPPING_METHOD_IN_STORE,
    SHIPPING_METHOD_HOME,
    PAYMENT_METHOD_PAYPAL,
    PAYMENT_METHOD_AFFIRM,
} from '@@client/checkout/checkout-utils'
import {bigBlueStyle} from '@@client/checkout/checkout-styles'
import {addNotificationNotice} from '@@client/notification/actions'
import {
    setIsGuest,
    intializeCheckout,
    clearContactInfoErrors,
    setCheckoutField,
    clearGoToCart,
    markGoToCart,
    completeOrder,
    resetCheckout,
} from '@@client/checkout/actions'
import {savePaypalOrderId} from '@@client/checkout/paypal'
import {saveAffirmOrderId} from '@@client/checkout/affirm'
import {clearCart} from '@@client/cart/actions'
import Layout from '@@client/app/Layout'
import Col100 from '@@client/components/Col100'
import GatherSection from '@@client/checkout/GatherSection'
import StepSpinner from '@@client/checkout/StepSpinner'
import SummaryLayout from '@@client/checkout/SummaryLayout'
import StepLogin from '@@client/checkout/StepLogin'
import StepShipping from '@@client/checkout/StepShipping'
import StepGuest from '@@client/checkout/StepGuest'
import StepUserContact from '@@client/checkout/StepUserContact'
import StepBilling from '@@client/checkout/StepBilling'

// TODO -- fill in shipping and billing info from previous order, if available
// TODO -- save shipping and billing info if user is logged in
// TODO -- save contact phone to account when logged in

const hasContactInfoErrors = (checkoutStore) => {
    const contactInfo = checkoutStore.contactInfo

    if(!contactInfo) return false

    if(contactInfo.errors && contactInfo.errors.length) return true

    if(contactInfo.paramErrors && !isEmptyObject(contactInfo.paramErrors)) return true

    return false
}

const StepEmptyLayout = ({children}) => {
    return (
        <>
            <div className='container'>
                <Col100>
                    <h1>Checkout</h1>

                    {children}
                </Col100>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        background: $c--gray--bg;
                        padding: 40px 0 100px;
                    }
                `}
            </style>
        </>
    )
}

const StepLoading = () => {
    return (
        <StepEmptyLayout>
            <div className='container' />
            <style jsx>
                {`
                    .container {
                        height: 100px;
                    }
                `}
            </style>
        </StepEmptyLayout>
    )
}

const StepEmpty = () => {
    return (
        <StepEmptyLayout>
            <p className={bigBlueStyle.className}>There are no items in your cart. <Link href='/'><a>Search for parts.</a></Link></p>

            {bigBlueStyle.styles}
        </StepEmptyLayout>
    )
}

const StepReport = ({desiredStep, stepToRender, firstIncompleteStep}) => {
    if(!isDev()) return null

    return (
        <div className='debug-report'>
            <p>
                {desiredStep ? <span>desired: {desiredStep}<br /></span> : null}
                {firstIncompleteStep ? <span>first incomplete: {firstIncompleteStep}<br /></span> : null}
                step: {stepToRender}
            </p>
        </div>
    )
}

const steps = {
    empty: {
        isIncluded: always(true),
        isComplete: ({checkoutStore}) => {
            if(!checkoutStore.info.checkoutTokenPayload) return false

            const items = checkoutStore.info.checkoutTokenPayload.items

            // complete if we have items
            return Boolean(items.length)
        },
    },
    login: {
        isIncluded: ({isLoggedIn}) => {
            // must visit this step if not logged in
            return !isLoggedIn
        },
        isComplete: ({checkoutStore, isLoggedIn}) => {
            // always complete if the user is logged in
            if(isLoggedIn) {
                return true
            }
            // complete if the user has chosen to checkout as a guest
            else {
                return Boolean(checkoutStore.isGuest)
            }
        },
    },
    guest: {
        isIncluded: ({isLoggedIn}) => {
            // must visit this step if not logged in
            return !isLoggedIn
        },
        isComplete: ({checkoutStore, isLoggedIn}) => {
            // if the user is logged in, then they never need to go to this step, so it is complete
            if(isLoggedIn) {
                return true
            }

            if(!checkoutStore.info.contactPassed || !checkoutStore.info.contactName || !checkoutStore.info.contactEmail || !checkoutStore.info.contactPhone) {
                return false
            }

            if(hasContactInfoErrors(checkoutStore)) {
                return false
            }

            return true
        },
    },
    userContact: {
        isIncluded: ({isLoggedIn, accountInfo}) => {
            const accountPhone = get('phone', null, accountInfo)

            // must visit this step if logged in but no account phone number is set
            return isLoggedIn && !accountPhone
        },
        isComplete: ({checkoutStore, isLoggedIn, accountInfo}) => {
            const accountPhone = get('phone', null, accountInfo)
            const contactPhone = checkoutStore.info.contactPhone

            // if the user is not logged in, then they never need to go to this step, so it is complete
            if(!isLoggedIn) {
                return true
            }

            if(hasContactInfoErrors(checkoutStore)) {
                return false
            }

            if(!accountPhone && !contactPhone) {
                return false
            }

            return true
        },
    },
    shipping: {
        isIncluded: always(true),
        isComplete: ({checkoutStore}) => {
            // if we're currently processing this step, then it is not complete
            if(
                checkoutStore.storePickupOptions.doing
                || checkoutStore.canHomeShip.doing
                || checkoutStore.homeShippingEstimate.doing
                || checkoutStore.salesTax.doing
            ) {
                return false
            }

            // if a shipping method has not been chosen, not complete
            if(!checkoutStore.info.shippingMethod) {
                return false
            }

            if(checkoutStore.info.shippingMethod === SHIPPING_METHOD_IN_STORE && (!checkoutStore.info.storePickupOptionsToken || !checkoutStore.info.pickupStoreId)) {
                return false
            }

            if(checkoutStore.info.shippingMethod === SHIPPING_METHOD_HOME && !checkoutStore.info.homeShippingEstimateToken) {
                return false
            }

            // we fetch sales tax at shipping, so it needs to be set in order to continue
            if(!checkoutStore.info.salesTaxToken) {
                return false
            }

            return true
        },
    },
    billing: {
        isIncluded: always(true),
        isComplete: ({checkoutStore}) => {
            // if we're currently processing this step, then it is not complete
            if(
                checkoutStore.payWithBandrAccount.doing
                || checkoutStore.payWithPaypal.doing
                || checkoutStore.payWithAffirm.doing
                || checkoutStore.payWithCreditCard.doing
            ) {
                return false
            }

            // if a payment method has not been chosen, not complete
            if(!checkoutStore.info.paymentMethod) {
                return false
            }

            if(!checkoutStore.info.paymentToken) {
                return false
            }

            return true
        },
    },
}

const stepOrder = Object.keys(steps)

// returns null if no incomplete steps
const getFirstIncompleteStep = ({checkoutStore, accountInfo, isLoggedIn}, stopAtStep = null) => {
    for(const stepName in steps) {
        if(stopAtStep && stepName === stopAtStep) {
            return stepName
        }

        const isIncluded = steps[stepName].isIncluded({checkoutStore, accountInfo, isLoggedIn})
        const isComplete = steps[stepName].isComplete({checkoutStore, accountInfo, isLoggedIn})

        if(isIncluded && !isComplete) {
            return stepName
        }
    }

    return null
}

const useCurrentStep = (desiredStep = null) => {
    const checkoutStore = useSelector(state => state.checkout)
    const isLoggedIn = useIsUser()
    const accountInfo = useAccountInfo()
    const [currentStep, setCurrentStep] = useState('loading')

    useEffect(() => {
        if(!checkoutStore.info.checkoutToken || checkoutStore.goToCart) {
            setCurrentStep('loading')
        }
        else {
            const incompleteStep = getFirstIncompleteStep({
                checkoutStore,
                isLoggedIn,
                accountInfo,
            }, desiredStep)

            setCurrentStep(
                incompleteStep === null
                    ? 'review'
                    : incompleteStep,
            )
        }
    }, [checkoutStore, isLoggedIn, accountInfo, desiredStep])

    return currentStep
}

const useFirstIncompleteStep = () => {
    const checkoutStore = useSelector(state => state.checkout)
    const isLoggedIn = useIsUser()
    const accountInfo = useAccountInfo()
    const [step, setStep] = useState(null)

    useEffect(() => {
        setStep(getFirstIncompleteStep({checkoutStore, isLoggedIn, accountInfo}))
    }, [checkoutStore, isLoggedIn, accountInfo])

    return step
}

const useStepPlan = () => {
    const buildStepPlan = () => {
        return map((stepInfo) => {
            return {
                isIncluded: stepInfo.isIncluded({checkoutStore, isLoggedIn, accountInfo}),
                isComplete: stepInfo.isComplete({checkoutStore, isLoggedIn, accountInfo}),
            }
        }, steps)
    }

    const checkoutStore = useSelector(state => state.checkout)
    const isLoggedIn = useIsUser()
    const accountInfo = useAccountInfo()
    const [plan, setPlan] = useState(buildStepPlan())

    useEffect(() => {
        setPlan(buildStepPlan())
    }, [checkoutStore, isLoggedIn, accountInfo])

    return plan
}

const stepIsAvailable = (stepOrder, step, firstIncompleteStep) => {
    if(firstIncompleteStep === null) return true

    const result = compareSteps(stepOrder, step, firstIncompleteStep)

    // step is at or before firstIncompleteStep
    return result === 0 || result === -1
}

// a step is open if it comes before the first incomplete step or it is the step to render
const stepIsOpen = (stepOrder, step, firstIncompleteStep, stepToRender) => {
    if(step === stepToRender) return true

    // comes before the firstIncompleteStep
    return compareSteps(stepOrder, step, firstIncompleteStep) === -1
}

export default function Page() {
    const router = useRouter()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const isLoggedIn = useIsUser()
    const tokenCache = useTokenCache()
    const stepRefs = {
        guest: useRef(null),
        userContat: useRef(null),
        shipping: useRef(null),
        billing: useRef(null),
        review: useRef(null),
    }
    const [clearAndRedirect, setClearAndRedirect] = useState(null) // set to a URL if you want to clear checkout and the shopping cart and redirect to another page
    const [forceDisabled, setForceDisabled] = useState(false)
    const [desiredStep, setDesiredStep] = useState(null)
    const stepToRender = useCurrentStep(desiredStep)
    const firstIncompleteStep = useFirstIncompleteStep()
    const stepPlan = useStepPlan()

    // if the user logs in, make sure they aren't checking out as a guest
    useEffect(() => {
        if(isLoggedIn) {
            dispatch(setIsGuest(false))
        }
    }, [isLoggedIn])

    useEffect(() => {
        if(stepRefs[stepToRender]) {
            scrollRefIntoView(stepRefs[stepToRender])
        }
    }, [stepToRender])

    useEffect(() => {
        // we're doing the clearAndRedirect here because if we clear the checkoutToken, this effect will get another one. yes, sort of a kludge, but need to get this done
        if(clearAndRedirect) {
            dispatch(resetCheckout(true))
            dispatch(clearCart())

            // e.g. a paypal approve url
            if(/^http/.test(clearAndRedirect)) {
                window.location.href = clearAndRedirect
            }
            else {
                routeToTop(router, clearAndRedirect)
            }
        }
        // if we need to go to the cart for some reason
        else if(checkoutStore.goToCart) {
            routeToTop(router, '/cart')
            dispatch(clearGoToCart())
        }
        // if the checkoutToken is cleared, try to get another one
        else if(!checkoutStore.info.checkoutToken) {
            dispatch(intializeCheckout(tokenCache))
                .then(success => {
                    if(!success) {
                        routeToTop(router, '/cart')
                    }
                })
        }
    }, [checkoutStore.goToCart, checkoutStore.info.checkoutToken, clearAndRedirect])

    // go back to the cart if the checkout token is expired
    useEffect(() => {
        if(checkoutStore.info.checkoutToken) {
            const tokenDecoded = decode(checkoutStore.info.checkoutToken)
            const now = getStampS()

            // checkout token is expired
            if(tokenDecoded.exp <= now) {
                dispatch(markGoToCart())
                dispatch(resetCheckout())
                dispatch(addNotificationNotice('Your checkout session has expired. Click the Checkout button to get started again.', {autoDismiss: true, dismissTimeout: 12000}))
            }
        }
    }, [])

    useEffect(() => {
        sendEvent('checkout--start')
    }, [])

    const handleContinueGuest = () => {
        sendEvent('checkout--login-or-continue')

        dispatch(setIsGuest(true))
        // always go to the guest step so the user must verify their contact info.
        setDesiredStep('guest')
    }

    const handleLogin = () => {
        sendEvent('checkout--login-or-continue')
        // clear the desired step in case it's set to 'login', so we don't stay on this step forever
        setDesiredStep(null)
    }

    const scrollRefIntoView = (ref) => {
        if(!ref.current) return

        ref.current.scrollIntoView(true)
    }

    const handleStepSubmit = stepName => () => {
        if(stepName === 'guest') {
            sendEvent('checkout--contact-info')
            setDesiredStep('shipping')
        }
        else if(stepName === 'userContact') {
            sendEvent('checkout--contact-info')
            setDesiredStep('shipping')
        }
        else if(stepName === 'shipping') {
            sendEvent('checkout--shipping')
            setDesiredStep('billing')
        }
        else if(stepName === 'billing') {
            sendEvent('checkout--payment')
            setDesiredStep('review')
        }
    }

    const handleGuestSubmit = ({contactName, contactEmail, contactPhone}) => {
        dispatch(clearContactInfoErrors())
        dispatch(setCheckoutField('contactName', contactName))
        dispatch(setCheckoutField('contactEmail', contactEmail))
        dispatch(setCheckoutField('contactPhone', contactPhone))
        dispatch(setCheckoutField('contactPassed', true))
        handleStepSubmit('guest')()
    }

    const handleUserContactSubmit = ({contactPhone}) => {
        dispatch(clearContactInfoErrors())
        dispatch(setCheckoutField('contactPhone', contactPhone))
        dispatch(setCheckoutField('contactPassed', true))
        handleStepSubmit('userContact')()
    }

    const handleEditClick = stepName => () => {
        setDesiredStep(stepName)
    }

    const stepAvailabilities = [
        'guest',
        'userContact',
        'shipping',
        'billing',
    ].reduce((acc, stepName) => {
        acc[stepName] = {
            isIncluded: stepPlan[stepName].isIncluded,
            isAvailable: stepIsAvailable(stepOrder, stepName, firstIncompleteStep),
            isOpen: stepIsOpen(stepOrder, stepName, firstIncompleteStep, stepToRender),
            isCurrent: stepToRender === stepName,
        }

        return acc
    }, {})

    const stepShouldShowSummary = (stepAvailability) => {
        // show summary if the step is available but not open (if it's open, we want to show the form)
        return stepAvailability.isIncluded && stepAvailability.isAvailable && !stepAvailability.isCurrent
    }

    const handleComplete = () => {
        setForceDisabled(true)

        dispatch(completeOrder(tokenCache))
            .then(success => {
                if(success) {
                    if(checkoutStore.info.paymentMethod === PAYMENT_METHOD_PAYPAL) {
                        sendEvent('checkout--complete--pending-payment')

                        savePaypalOrderId(checkoutStore.info.paymentTokenPayload.paypalOrderId)
                        setClearAndRedirect(checkoutStore.info.paymentTokenPayload.approveUrl)
                    }
                    else if(checkoutStore.info.paymentMethod === PAYMENT_METHOD_AFFIRM) {
                        sendEvent('checkout--complete--pending-payment')

                        saveAffirmOrderId(checkoutStore.info.paymentTokenPayload.affirmOrderId)
                        setClearAndRedirect(checkoutStore.info.paymentTokenPayload.approveUrl)
                    }
                    else {
                        sendEvent('checkout--complete')

                        setClearAndRedirect('/checkout/complete')
                    }
                }
                else {
                    setForceDisabled(false)
                }
            })
    }

    const disabled = checkoutStore.loading || forceDisabled

    const renderSteps = (stepToRender) => {
        if(!stepToRender || stepToRender === 'loading') {
            return <StepLoading />
        }
        else if(stepToRender === 'empty') {
            return <StepEmpty />
        }
        else if(stepToRender === 'login') {
            return <StepLogin onGuest={handleContinueGuest} onLogin={handleLogin} />
        }
        // includes the 'review' step
        else {
            // the shipping and billing steps need to start at 2 if rendering the guest or userContact step
            const restStepsStartAt = stepAvailabilities.guest.isIncluded || stepAvailabilities.userContact.isIncluded
                ? 2
                : 1

            return (
                <SummaryLayout ref={stepRefs.review} checkoutStore={checkoutStore} disabled={disabled || stepToRender !== 'review'} onComplete={handleComplete}>
                    <GatherSection
                        ref={stepRefs.guest}
                        isIncluded={stepAvailabilities.guest.isIncluded}
                        isAvailable={stepAvailabilities.guest.isAvailable}
                        isOpen={stepAvailabilities.guest.isOpen}
                        number={1}
                        title='Contact Information'
                        showEdit={stepShouldShowSummary(stepAvailabilities.guest)}
                        onEditClick={handleEditClick('guest')}
                        editTitle='Edit Contact Information'
                    >
                        <StepGuest
                            showSummary={stepShouldShowSummary(stepAvailabilities.guest)}
                            onSubmit={handleGuestSubmit}
                            disabled={disabled}
                            setDesiredStep={setDesiredStep}
                            contactName={checkoutStore.info.contactName}
                            contactEmail={checkoutStore.info.contactEmail}
                            contactPhone={checkoutStore.info.contactPhone}
                            errors={checkoutStore.contactInfo.errors}
                            paramErrors={checkoutStore.contactInfo.paramErrors}
                        />
                    </GatherSection>
                    <GatherSection
                        ref={stepRefs.userContact}
                        isIncluded={stepAvailabilities.userContact.isIncluded}
                        isAvailable={stepAvailabilities.userContact.isAvailable}
                        isOpen={stepAvailabilities.userContact.isOpen}
                        number={1}
                        title='Contact Information'
                        showEdit={stepShouldShowSummary(stepAvailabilities.userContact)}
                        onEditClick={handleEditClick('userContact')}
                        editTitle='Edit Contact Information'
                    >
                        <StepUserContact
                            showSummary={stepShouldShowSummary(stepAvailabilities.userContact)}
                            onSubmit={handleUserContactSubmit}
                            disabled={disabled}
                            contactPhone={checkoutStore.info.contactPhone}
                            errors={checkoutStore.contactInfo.errors}
                            paramErrors={checkoutStore.contactInfo.paramErrors}
                        />
                    </GatherSection>
                    <GatherSection
                        ref={stepRefs.shipping}
                        isIncluded={stepAvailabilities.shipping.isIncluded}
                        isAvailable={stepAvailabilities.shipping.isAvailable}
                        isOpen={stepAvailabilities.shipping.isOpen}
                        number={restStepsStartAt}
                        title='Shipping Information'
                        showEdit={stepShouldShowSummary(stepAvailabilities.shipping)}
                        onEditClick={handleEditClick('shipping')}
                        editTitle='Edit Shipping Information'
                    >
                        <StepShipping
                            showSummary={stepShouldShowSummary(stepAvailabilities.shipping)}
                            onSubmit={handleStepSubmit('shipping')}
                            disabled={disabled}
                        />
                    </GatherSection>
                    <GatherSection
                        ref={stepRefs.billing}
                        isIncluded={stepAvailabilities.billing.isIncluded}
                        isAvailable={stepAvailabilities.billing.isAvailable}
                        isOpen={stepAvailabilities.billing.isOpen}
                        number={restStepsStartAt + 1}
                        title='Payment Information'
                        showEdit={stepShouldShowSummary(stepAvailabilities.billing)}
                        onEditClick={handleEditClick('billing')}
                        editTitle='Edit Billing Information'
                    >
                        <StepBilling
                            showSummary={stepShouldShowSummary(stepAvailabilities.billing)}
                            onSubmit={handleStepSubmit('billing')}
                            disabled={disabled}
                        />
                    </GatherSection>
                </SummaryLayout>
            )
        }
    }

    return (
        <Layout title='Checkout'>
            <StepReport desiredStep={desiredStep} stepToRender={stepToRender} firstIncompleteStep={firstIncompleteStep} />
            <div className='container'>
                <StepSpinner loading={disabled} title='Processing' message='This step of your order is being processed.' />
                {checkoutStore.initialize.doing ? null : renderSteps(stepToRender)}
            </div>
        </Layout>
    )
}
